import {
    idImagem,
    imagensUsadas,
    PROTOCOLO_IMAGEM,
    tokenImagem,
} from '@acme/core'

const TOKEN_ICONE = /:lucide\[([a-z0-9-]+)\]/g

export { idImagem, imagensUsadas, PROTOCOLO_IMAGEM, tokenImagem }

export type MarkdownSlice =
    | { tipo: 'texto'; valor: string }
    | { tipo: 'icone'; valor: string }

/** Divide o Markdown em pedaços, separando por texto ou ícone */
export function fatiarMarkdown(texto: string): MarkdownSlice[] {
    const pedacos: MarkdownSlice[] = []
    let ultimoFim = 0

    for (const match of texto.matchAll(TOKEN_ICONE)) {
        const inicio = match.index ?? 0

        if (inicio > ultimoFim) {
            pedacos.push({
                tipo: 'texto',
                valor: texto.slice(ultimoFim, inicio),
            })
        }

        pedacos.push({ tipo: 'icone', valor: match[1] })
        ultimoFim = inicio + match[0].length
    }

    if (ultimoFim < texto.length) {
        pedacos.push({ tipo: 'texto', valor: texto.slice(ultimoFim) })
    }

    return pedacos
}

/** Retona o prefixo para um ícone do Lucide */
export function tokenLucideIcon(nome: string) {
    return `:lucide[${nome}]`
}

export function inserirNoTexto(
    texto: string,
    abertura: string,
    inicio: number,
    fim: number = inicio,
    fechamento = '',
) {
    const selecionado = texto.slice(inicio, fim)
    const novo =
        texto.slice(0, inicio) +
        abertura +
        selecionado +
        fechamento +
        texto.slice(fim)

    // Sem seleção o cursor para entre os marcadores, que é onde o usuário vai
    // digitar; com seleção ele vai para depois do fechamento, liberando a
    // escrita na sequência do trecho formatado.
    const cursor = selecionado
        ? inicio + abertura.length + selecionado.length + fechamento.length
        : inicio + abertura.length

    return { texto: novo, cursor }
}

/**
 * Aplica o prefixo de bloco (cabeçalho, marcador de lista, tarefa) nas linhas
 * tocadas pela seleção, ou o remove quando todas elas já o têm.
 *
 * @param montar - Recebe a ordem da linha dentro do bloco, para a lista numerada.
 * @param padrao - Reconhece o prefixo já aplicado; não pode ser global.
 * @returns O texto novo e a seleção que cobre o bloco alterado — sem seleção,
 * `inicio` e `fim` apontam para o fim da linha, onde o usuário continua digitando.
 */
export function alternarPrefixo(
    texto: string,
    montar: (ordem: number) => string,
    padrao: RegExp,
    inicio: number,
    fim: number = inicio,
) {
    // `lastIndexOf` trata o índice negativo como 0 e acharia a quebra da
    // posição 0, jogando o bloco para a segunda linha.
    const inicioBloco =
        inicio === 0 ? 0 : texto.lastIndexOf('\n', inicio - 1) + 1
    const quebraSeguinte = texto.indexOf('\n', fim)
    const fimBloco = quebraSeguinte === -1 ? texto.length : quebraSeguinte

    const linhas = texto.slice(inicioBloco, fimBloco).split('\n')

    // Alternar só desmarca o bloco todo marcado: com uma linha solta ainda sem
    // o prefixo, o clique termina de marcar o bloco em vez de desfazê-lo.
    const isBlocoMarcado = linhas.every(linha => padrao.test(linha))

    const bloco = linhas
        .map((linha, ordem) =>
            isBlocoMarcado ? linha.replace(padrao, '') : montar(ordem) + linha,
        )
        .join('\n')

    const fimSelecao = inicioBloco + bloco.length

    return {
        texto: texto.slice(0, inicioBloco) + bloco + texto.slice(fimBloco),
        inicio: inicio === fim ? fimSelecao : inicioBloco,
        fim: fimSelecao,
    }
}
